import React from 'react';
import { motion } from 'framer-motion';
import { FaExchangeAlt, FaCar, FaMapMarkerAlt, FaUser } from 'react-icons/fa';

const UpcomingReturns = ({ rentals }) => {
  // Get the current date
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Find active rentals with future return dates (picked up but not yet returned)
  const upcomingReturns = rentals
    .filter(rental => {
      const pickupDate = new Date(rental.pickupDateTime);
      const returnDate = new Date(rental.returnDateTime);
      return rental.status === 'confirmed' && 
             pickupDate <= today && 
             returnDate >= today;
    })
    .sort((a, b) => new Date(a.returnDateTime) - new Date(b.returnDateTime))
    .slice(0, 5); // Show top 5 upcoming returns
  
  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Calculate days from today
  const getDaysFromToday = (dateString) => {
    const date = new Date(dateString);
    const diffTime = date - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return `In ${diffDays} days`;
  };
  
  // Get color based on how soon the return is
  const getReturnColor = (dateString) => {
    const date = new Date(dateString);
    const diffTime = date - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 1) return 'text-red-600 bg-red-50';
    if (diffDays < 2) return 'text-orange-600 bg-orange-50';
    if (diffDays < 4) return 'text-amber-600 bg-amber-50';
    return 'text-blue-600 bg-blue-50';
  };
  
  return (
    <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
      {upcomingReturns.length > 0 ? (
        upcomingReturns.map((rental, index) => (
          <motion.div
            key={rental._id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            className="flex items-center p-3 rounded-lg border border-gray-100 hover:shadow-md transition-all cursor-pointer"
            whileHover={{ scale: 1.02 }}
          >
            <div className={`flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full ${getReturnColor(rental.returnDateTime)}`}>
              <FaExchangeAlt size={16} />
            </div>
            <div className="ml-3 flex-grow">
              <div className="flex justify-between items-start">
                <p className="font-medium text-sm text-gray-900">
                  {rental.carName}
                </p>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getReturnColor(rental.returnDateTime)}`}>
                  {getDaysFromToday(rental.returnDateTime)}
                </span>
              </div>
              <div className="mt-1 text-xs text-gray-500 flex items-center">
                <FaUser className="mr-1" /> 
                {rental.customer.firstName} {rental.customer.lastName}
              </div>
              <div className="mt-1 text-xs text-gray-500 flex items-center">
                <FaExchangeAlt className="mr-1" /> 
                {formatDate(rental.returnDateTime)}
              </div>
              <div className="mt-1 text-xs text-gray-500 flex items-center">
                <FaMapMarkerAlt className="mr-1" /> 
                {rental.returnLocation}
              </div>
            </div>
          </motion.div>
        ))
      ) : (
        <div className="text-center py-8 text-gray-500">
          <FaExchangeAlt className="mx-auto text-gray-300 mb-2" size={24} />
          <p>No active rentals with upcoming returns</p>
        </div>
      )}
    </div>
  );
};

export default UpcomingReturns;
